import { useMemo, useState } from 'react';
import {
  Calculator,
  Coffee,
  GraduationCap,
  Home,
  Sparkles,
  TrendingDown,
  TrendingUp,
  Users,
} from 'lucide-react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { KpiCard, PageContainer, SectionTitle } from '@/components/design-system';
import { formatDA } from '@/lib/format';

// Sprint 11.0 — Simulateur de bénéfice formation B2B (séminaires inter-entreprises).
// Modélise la grille KOST :
//   - Inputs prestation : nb participants, durée jours, prix groupe HT
//   - Charges directes : formateur (HT) + IRG retenu (% du formateur)
//   - Charges variables : pack & déjeuner par participant
//   - Charges fixes : prime collaboratrice, location salle, immobilisations commerciales (%)
//   - Note : la location salle peut inclure eau + électricité selon le contrat

const COLORS = {
  formateur: '#8b5cf6',
  irg: '#a78bfa',
  pack: '#06b6d4',
  dejeuner: '#f59e0b',
  prime: '#ec4899',
  salle: '#10b981',
  immobil: '#6366f1',
};

export function SimulateurB2bPage(): JSX.Element {
  // Inputs prestation
  const [nbParticipants, setNbParticipants] = useState(14);
  const [dureeJours, setDureeJours] = useState(2);
  const [prixGroupe, setPrixGroupe] = useState(653240);

  // Charges
  const [formateur, setFormateur] = useState(140000);
  const [irgPct, setIrgPct] = useState(10);
  const [packParPersonne, setPackParPersonne] = useState(1350); // 18900/14 ≈ 1350
  const [dejeunerParPersonne, setDejeunerParPersonne] = useState(4286); // 60000/14 ≈ 4286
  const [primeCollab, setPrimeCollab] = useState(14000);
  const [locationSalle, setLocationSalle] = useState(11000);
  const [salleIncluEauElec, setSalleIncluEauElec] = useState(true);
  const [immobilPct, setImmobilPct] = useState(0.7); // 4600 / 653240 ≈ 0.7%

  // Calculs dérivés
  const irgMontant = useMemo(() => Math.round(formateur * (irgPct / 100)), [formateur, irgPct]);
  const packTotal = useMemo(
    () => packParPersonne * nbParticipants,
    [packParPersonne, nbParticipants],
  );
  const dejeunerTotal = useMemo(
    () => dejeunerParPersonne * nbParticipants,
    [dejeunerParPersonne, nbParticipants],
  );
  const immobilMontant = useMemo(
    () => Math.round(prixGroupe * (immobilPct / 100)),
    [prixGroupe, immobilPct],
  );

  const totalCharges = useMemo(
    () =>
      formateur +
      irgMontant +
      packTotal +
      dejeunerTotal +
      primeCollab +
      locationSalle +
      immobilMontant,
    [formateur, irgMontant, packTotal, dejeunerTotal, primeCollab, locationSalle, immobilMontant],
  );

  const beneficeKost = prixGroupe - totalCharges;
  const marge = prixGroupe > 0 ? (beneficeKost / prixGroupe) * 100 : 0;
  const prixParPersonne = nbParticipants > 0 ? prixGroupe / nbParticipants : 0;

  const chargesPie = [
    { name: 'Formateur', value: formateur, color: COLORS.formateur },
    { name: 'IRG retenu', value: irgMontant, color: COLORS.irg },
    { name: 'Pack participants', value: packTotal, color: COLORS.pack },
    { name: 'Déjeuner', value: dejeunerTotal, color: COLORS.dejeuner },
    { name: 'Prime collaboratrice', value: primeCollab, color: COLORS.prime },
    { name: 'Location salle', value: locationSalle, color: COLORS.salle },
    { name: 'Immobilisations', value: immobilMontant, color: COLORS.immobil },
  ].filter((c) => c.value > 0);

  return (
    <PageContainer>
      <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
            <Calculator className="h-6 w-6 text-violet-600" />
            Simulateur Bénéfice B2B
          </h1>
          <p className="text-sm text-muted-foreground">
            Estime la marge nette d&apos;un séminaire inter-entreprise avant signature.
          </p>
        </div>
      </header>

      {/* KPIs résultat en haut */}
      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard label="Prix groupe HT" value={formatDA(prixGroupe)} variant="finances" />
        <KpiCard label="Total charges" value={formatDA(totalCharges)} variant="echeances" />
        <KpiCard
          label="Bénéfice KOST"
          value={formatDA(beneficeKost)}
          variant={beneficeKost >= 0 ? 'formation_b2b' : 'echeances'}
        />
        <KpiCard
          label="Marge nette"
          value={`${marge.toFixed(1)} %`}
          variant={marge >= 30 ? 'formation_b2b' : marge >= 15 ? 'echeances' : 'crm'}
        />
      </div>

      <div className="grid gap-5 lg:grid-cols-[1fr_360px]">
        {/* Colonne saisie */}
        <div className="space-y-5">
          {/* Prestation */}
          <Section
            icon={<Users className="h-4 w-4 text-blue-600" />}
            title="Prestation"
            description="Caractéristiques du séminaire"
          >
            <NumField
              label="Nombre de participants"
              value={nbParticipants}
              onChange={setNbParticipants}
              min={1}
              hint={
                prixParPersonne > 0
                  ? `${formatDA(Math.round(prixParPersonne))} / participant`
                  : undefined
              }
            />
            <NumField
              label="Durée (jours)"
              value={dureeJours}
              onChange={setDureeJours}
              min={1}
              max={30}
            />
            <NumField
              label="Prix groupe HT"
              value={prixGroupe}
              onChange={setPrixGroupe}
              step={1000}
              suffix="DA"
              hint="Montant facturé au client (HT)"
            />
          </Section>

          {/* Charges directes */}
          <Section
            icon={<GraduationCap className="h-4 w-4 text-violet-600" />}
            title="Charges directes"
            description="Formateur + impôt à la source"
          >
            <NumField
              label="Honoraires formateur (HT)"
              value={formateur}
              onChange={setFormateur}
              step={1000}
              suffix="DA"
            />
            <NumField
              label="IRG retenu à la source"
              value={irgPct}
              onChange={setIrgPct}
              step={0.5}
              max={100}
              suffix="%"
              hint={`= ${formatDA(irgMontant)} retenu sur les honoraires`}
            />
          </Section>

          {/* Charges variables */}
          <Section
            icon={<Coffee className="h-4 w-4 text-amber-600" />}
            title="Charges variables"
            description={`Auto-calculées × ${nbParticipants} participants`}
          >
            <NumField
              label="Pack par participant"
              value={packParPersonne}
              onChange={setPackParPersonne}
              step={100}
              suffix="DA"
              hint={`Total pack : ${formatDA(packTotal)}`}
            />
            <NumField
              label="Déjeuner par participant"
              value={dejeunerParPersonne}
              onChange={setDejeunerParPersonne}
              step={100}
              suffix="DA"
              hint={`Total déjeuner : ${formatDA(dejeunerTotal)}`}
            />
          </Section>

          {/* Charges fixes */}
          <Section
            icon={<Home className="h-4 w-4 text-emerald-600" />}
            title="Charges fixes"
            description="Prime, location, immobilisations"
          >
            <NumField
              label="Prime collaboratrice"
              value={primeCollab}
              onChange={setPrimeCollab}
              step={500}
              suffix="DA"
              hint="Forfait pour le commercial qui a signé"
            />
            <NumField
              label="Location salle"
              value={locationSalle}
              onChange={setLocationSalle}
              step={500}
              suffix="DA"
            />
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={salleIncluEauElec}
                onChange={(e) => setSalleIncluEauElec(e.target.checked)}
                className="h-3.5 w-3.5"
              />
              <span>Eau + électricité incluses dans la location</span>
            </label>
            <NumField
              label="Immobilisations commerciales"
              value={immobilPct}
              onChange={setImmobilPct}
              step={0.1}
              max={100}
              suffix="%"
              hint={`= ${formatDA(immobilMontant)} (du prix groupe)`}
            />
          </Section>
        </div>

        {/* Colonne résultat */}
        <aside className="space-y-4">
          <div
            className={`rounded-2xl border-2 p-5 shadow-sm ${
              beneficeKost >= 0
                ? 'border-emerald-300 bg-gradient-to-br from-emerald-50 to-emerald-100/50'
                : 'border-rose-300 bg-gradient-to-br from-rose-50 to-rose-100/50'
            }`}
          >
            <div className="flex items-center gap-2">
              {beneficeKost >= 0 ? (
                <TrendingUp className="h-5 w-5 text-emerald-700" />
              ) : (
                <TrendingDown className="h-5 w-5 text-rose-700" />
              )}
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-700">
                Bénéfice KOST
              </span>
            </div>
            <div
              className={`num mt-2 text-3xl font-bold ${
                beneficeKost >= 0 ? 'text-emerald-700' : 'text-rose-700'
              }`}
            >
              {formatDA(beneficeKost)}
            </div>
            <div className="mt-1 text-xs text-slate-600">
              Marge : <strong>{marge.toFixed(1)} %</strong>
              {marge >= 40 && ' 🚀 excellente'}
              {marge >= 25 && marge < 40 && ' ✅ bonne'}
              {marge >= 15 && marge < 25 && ' ⚠️ correcte'}
              {marge < 15 && ' 🔴 faible'}
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
              <div className="rounded-md bg-white/60 p-2">
                <div className="text-slate-500">Vente</div>
                <div className="num font-bold text-slate-900">{formatDA(prixGroupe)}</div>
              </div>
              <div className="rounded-md bg-white/60 p-2">
                <div className="text-slate-500">Charges</div>
                <div className="num font-bold text-rose-700">−{formatDA(totalCharges)}</div>
              </div>
            </div>
          </div>

          {/* Pie chart répartition charges */}
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <SectionTitle>
              <span className="inline-flex items-center gap-2">
                <Sparkles className="h-3.5 w-3.5 text-violet-600" />
                Répartition des charges
              </span>
            </SectionTitle>
            {totalCharges > 0 ? (
              <>
                <div className="mt-3 h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={chargesPie}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={75}
                        paddingAngle={2}
                      >
                        {chargesPie.map((c) => (
                          <Cell key={c.name} fill={c.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(v: number) => formatDA(v)}
                        contentStyle={{ fontSize: 11, borderRadius: 8 }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <ul className="mt-2 space-y-1 text-[11px]">
                  {chargesPie.map((c) => {
                    const pct = (c.value / totalCharges) * 100;
                    return (
                      <li key={c.name} className="flex items-center justify-between gap-2">
                        <span className="flex items-center gap-1.5">
                          <span
                            className="h-2.5 w-2.5 rounded-sm"
                            style={{ backgroundColor: c.color }}
                          />
                          {c.name}
                        </span>
                        <span className="font-mono text-slate-600">
                          {formatDA(c.value)}
                          <span className="ml-1 text-[9px] text-slate-400">
                            ({pct.toFixed(1)}%)
                          </span>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </>
            ) : (
              <p className="mt-3 text-xs text-muted-foreground">
                Saisis les charges pour voir la répartition.
              </p>
            )}
          </div>

          <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-[11px] text-blue-900">
            💡 <strong>Astuce</strong> : pour un séminaire 14 personnes × 2 jours, la grille KOST de
            référence donne ~60 % de marge si la location inclut eau + électricité. Si la marge
            calculée ici est en dessous, vérifie le formateur ou les frais variables.
          </div>
        </aside>
      </div>
    </PageContainer>
  );
}

// ===== Sous-composants =====

function Section({
  icon,
  title,
  description,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  description?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4">
      <header className="mb-3 flex items-start gap-2.5">
        <div className="mt-0.5">{icon}</div>
        <div>
          <h2 className="text-sm font-bold text-slate-900">{title}</h2>
          {description && <p className="mt-0.5 text-[11px] text-muted-foreground">{description}</p>}
        </div>
      </header>
      <div className="space-y-2.5">{children}</div>
    </section>
  );
}

function NumField({
  label,
  value,
  onChange,
  step = 1,
  min = 0,
  max,
  suffix,
  hint,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  step?: number;
  min?: number;
  max?: number;
  suffix?: string;
  hint?: string;
}): JSX.Element {
  return (
    <label className="block">
      <span className="block text-[11px] font-semibold text-slate-700">{label}</span>
      <div className="mt-1 flex items-center gap-2">
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          step={step}
          min={min}
          max={max}
          className="num h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-right text-xs tabular-nums shadow-sm focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200"
        />
        {suffix && <span className="text-[11px] font-semibold text-slate-500">{suffix}</span>}
      </div>
      {hint && <span className="mt-0.5 block text-[10px] text-slate-500">{hint}</span>}
    </label>
  );
}
